import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Star, TrendingUp, Shield, ArrowRight, Check, Building2 } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { services } from '@/data/mock';
import Seo from '@/components/Seo';

const iconMap = {
  MapPin: MapPin,
  Star: Star,
  TrendingUp: TrendingUp,
  Shield: Shield,
  Building2: Building2,
};

export default function ServicesPage() {
  return (
    <Layout>
      <Seo
        title="Services"
        description="Explore yBro services: GBP management, review response, local SEO, ads, and UK DMCC compliance support."
        path="/services"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-ybro-light/50 to-white py-24 lg:py-32">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-ybro-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-ybro-primary font-semibold text-sm uppercase tracking-wider mb-4 block font-body">Our Services</span>
            <h1 className="text-4xl lg:text-5xl font-bold font-heading text-slate-900 mb-6">
              Our Core Services
            </h1>
            <p className="text-xl text-slate-500 leading-relaxed font-body">
              Choose a single service or combine them into a fully managed local growth package, tailored for your industry and locations.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div 
                  key={service.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}
                >
                  {/* Content */}
                  <div className={!isEven ? 'lg:order-2' : ''}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 rounded-2xl bg-ybro-light flex items-center justify-center">
                        {IconComponent && <IconComponent className="w-7 h-7 text-ybro-primary" />}
                      </div>
                      <span className="text-sm font-semibold font-body text-ybro-dark uppercase tracking-wider">
                        {service.shortTitle}
                      </span>
                    </div>

                    <h2 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 mb-6">
                      {service.title}
                    </h2>

                    <p className="text-lg text-slate-500 leading-relaxed mb-8 font-body">
                      {service.fullDescription}
                    </p>

                    <div className="space-y-3">
                      {service.inclusions.map((inclusion, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full bg-ybro-light flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-ybro-primary" />
                          </div>
                          <span className="text-slate-600 font-body">{inclusion}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Visual Card */}
                  <div className={!isEven ? 'lg:order-1' : ''}>
                    <Card className="overflow-hidden border-slate-200 shadow-xl shadow-ybro-primary/5">
                      <div className="h-4 bg-gradient-to-r from-ybro-primary to-ybro-dark" />
                      <CardHeader className="bg-ybro-light/30 border-b border-ybro-light">
                        <CardTitle className="text-2xl font-heading text-slate-900">{service.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="p-8">
                        <CardDescription className="text-base text-slate-500 leading-relaxed mb-6 font-body">
                          {service.description}
                        </CardDescription>
                        <div className="flex flex-wrap gap-3">
                          {service.title === "DMCC Act Compliance & Risk Audit" ? (
                            <>
                              <span className="px-3 py-1.5 rounded-full bg-ybro-light text-ybro-dark text-sm font-medium font-body">
                                UK Only
                              </span>
                              <span className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-sm font-medium font-body">
                                Fake Review Monitor
                              </span>
                              <span className="px-3 py-1.5 rounded-full bg-ybro-light/50 text-ybro-dark text-sm font-medium font-body">
                                Dispute Evidence
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="px-3 py-1.5 rounded-full bg-ybro-light text-ybro-dark text-sm font-medium font-body">
                                DMCC Compliant
                              </span>
                              <span className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-sm font-medium font-body">
                                UK • US • India
                              </span>
                              <span className="px-3 py-1.5 rounded-full bg-ybro-light/50 text-ybro-dark text-sm font-medium font-body">
                                Results Driven
                              </span>
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-ybro-light/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 mb-6">
            Ready to elevate your local presence?
          </h2>
          <p className="text-lg text-slate-500 mb-10 max-w-2xl mx-auto font-body">
            Get a free consultation to discuss which services are right for your business.
          </p>
          <Button 
            asChild
            size="lg"
            className="bg-ybro-primary hover:bg-ybro-dark text-white shadow-xl shadow-ybro-primary/25 hover:shadow-ybro-primary/40 transition-all duration-300 text-base px-8 py-6 h-auto font-body"
          >
            <Link to="/contact" className="flex items-center gap-2">
              Get Your Free Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
}
